import {PaginationConfig} from '@component/steps-wizard/pagination/steps-wizard-pagination.component';
import {PaginationButton, PaginationMappedPageEvent, PaginationPageButton} from '@component/pagination/pagination.component';
import {BehaviorSubject, Observable, OperatorFunction, Subscription} from 'rxjs';
import * as uuid from 'uuid';
import {filter} from 'rxjs/operators';

export interface StepsWizardEntry extends PaginationPageButton {
    // Expand with steps wizard logic...
    hasAnswer: boolean;
    inProgress: boolean;
    isTeacher: boolean;
}

class StepsWizardAdapterBuilder {
    readonly #config: PaginationConfig;

    constructor(entries: StepsWizardEntry[]) {
        this.#config = {id: uuid.v4(), itemsPerPage: 1, entries, currentPage: 1, maxSize: 0};
    }

    build(): StepsWizardAdapter {
        return new StepsWizardAdapter(this);
    }

    getId(): string {
        return this.#config.id;
    }

    getItemsPerPage(): number {
        return this.#config.itemsPerPage;
    }

    getEntries(): StepsWizardEntry[] {
        return this.#config.entries;
    }

    getCurrentPage(): number {
        return this.#config.currentPage;
    }

    setCurrentPage(page: number): StepsWizardAdapterBuilder {
        this.#config.currentPage = page;

        return this;
    }

    getMaxSize(): number {
        return this.#config.maxSize;
    }

    setPaginationMaxSize(maxSize: number): StepsWizardAdapterBuilder {
        this.#config.maxSize = maxSize;

        return this;
    }

    setLabels(previous: PaginationButton, next: PaginationButton): StepsWizardAdapterBuilder {
        this.#config.buttons = {previous, next};

        return this;
    }

    getPreviousButton(): PaginationButton | undefined {
        return this.#config.buttons?.previous;
    }

    getNextButton(): PaginationButton | undefined {
        return this.#config.buttons?.next;
    }

    setHidePages(value: boolean): StepsWizardAdapterBuilder {
        this.#config.hidePages = value;

        return this;
    }

    getHidePages(): boolean {
        return this.#config.hidePages ?? false;
    }
}

export class StepsWizardAdapter {
    readonly #subscriptions: Subscription[] = [];

    readonly #id: string;

    readonly #itemsPerPage: number;

    readonly #maxSize: number;

    readonly #entries: StepsWizardEntry[];

    readonly #hidePages: BehaviorSubject<boolean>;

    readonly #nextButtonSubject: BehaviorSubject<PaginationButton | undefined>;

    readonly #previousButtonSubject: BehaviorSubject<PaginationButton | undefined>;

    readonly #pageSubject: BehaviorSubject<number>;

    readonly #mappedPageSubject: BehaviorSubject<PaginationMappedPageEvent | undefined> = new BehaviorSubject<PaginationMappedPageEvent | undefined>(undefined);

    #currentPage: number;

    constructor(builder: StepsWizardAdapterBuilder) {
        this.#id = builder.getId();
        this.#entries = builder.getEntries();
        this.#itemsPerPage = builder.getItemsPerPage();
        this.#pageSubject = new BehaviorSubject<number>(this.#currentPage = builder.getCurrentPage());
        this.#maxSize = builder.getMaxSize();
        this.#hidePages = new BehaviorSubject<boolean>(builder.getHidePages());
        this.#previousButtonSubject = new BehaviorSubject<PaginationButton | undefined>(builder.getPreviousButton());
        this.#nextButtonSubject = new BehaviorSubject<PaginationButton | undefined>(builder.getNextButton());
    }

    get id(): string {
        return this.#id;
    }

    get entries(): StepsWizardEntry[] {
        return this.#entries;
    }

    set currentPage(value: number) {
        this.#pageSubject.next(this.#currentPage = value);
    }

    get currentPage(): number {
        return this.#currentPage;
    }

    get itemsPerPage(): number {
        return this.#itemsPerPage;
    }

    get maxSize(): number {
        return this.#maxSize;
    }

    get nextButton(): BehaviorSubject<PaginationButton | undefined> {
        return this.#nextButtonSubject;
    }

    get previousButton(): BehaviorSubject<PaginationButton | undefined> {
        return this.#previousButtonSubject;
    }

    get hidePages(): Observable<boolean> {
        return this.#hidePages;
    }

    subscribeToPageChange(fn: (page: number) => void): Subscription {
        const subscription: Subscription = this.#pageSubject.subscribe((page: number) => fn(page));

        this.#subscriptions.push(subscription);

        return subscription;
    }

    setPage(page: number): StepsWizardAdapter {
        this.#pageSubject.next(page);

        return this;
    }

    updateNextButton(button: PaginationButton): StepsWizardAdapter {
        this.#nextButtonSubject.next(button);

        return this;
    }

    updatePreviousButton(button: PaginationButton): StepsWizardAdapter {
        this.#previousButtonSubject.next(button);

        return this;
    }

    onMappedPageEvent(event: PaginationMappedPageEvent): StepsWizardAdapter {
        this.#mappedPageSubject.next(event);

        return this;
    }

    subscribeToMappedPageEvent(fn: (event: PaginationMappedPageEvent) => void): Subscription {
        const subscription: Subscription = this.#mappedPageSubject.pipe(
            filter((event: PaginationMappedPageEvent): boolean => undefined !== event) as OperatorFunction<PaginationMappedPageEvent | undefined, PaginationMappedPageEvent>,
        ).subscribe((event: PaginationMappedPageEvent) => fn(event));

        this.#subscriptions.push(subscription);

        return subscription;
    }

    destroy(): void {
        this.#subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    static Builder(steps: StepsWizardEntry[]): StepsWizardAdapterBuilder {
        return new StepsWizardAdapterBuilder(steps);
    }
}
